import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Typography,
  Stack,
  Autocomplete,
  TextField,
  Box,
  Button,
  Alert,
  Tooltip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import dayjs from 'dayjs';

import {
  ActionItem,
  Unit,
  Lease,
} from '@urbanmix-tech/shared-js';

import {
  saveLease,
} from '@/data'

import { getLongNumberStr, convertPseudoDateToRealDate } from '@/helpers/common';
import { exportSingleUnitToSpreadsheet } from '@/helpers/spreadsheetExport';

// Redux
import { useDispatch } from 'react-redux';
import {
  setBuildingVisual,
  setHighlightedUnits,
  setSelectedUnit,
} from '@/features/project/projectSlice';
import {
  ColorGradeVisualizer,
} from '@urbanmix-tech/um-embedded';

import { GenericDistinctVisualizer, colorScales } from './BuildingVisualizers';
import type { ColorScaleOptions } from './BuildingVisualizers';
import { ComparablesSection } from './PricingTool/ComparablesSection';
import { calculateEffectivePPSF } from './PricingTool/comparablesUtils';
import { withLegendConfig, commonLegendConfigs } from '../BuildingModel/visualizerConfig';
import { calculateNewLeaseDates } from '@/helpers/leaseHelpers';

type PricingToolTabContentProps = {
  units: Unit[];
  leases?: Lease[];
  actionItems: ActionItem[];
  hoveredUnitId: string | null;
  onSelectUnit(unitId: string | null): void;
  onHoverUnit(unitId: string | null): void;
  onCreateDraftLease?(draftLease: Lease): void;
  editingDraftLease?: any;
  onStopEditingDraftLease?(): void;
  initialUnitId?: string;
};

export function PricingToolTabContent({
  units,
  leases,
  actionItems,
  hoveredUnitId,
  onSelectUnit,
  onHoverUnit,
  onCreateDraftLease,
  editingDraftLease,
  onStopEditingDraftLease,
  initialUnitId,
}: PricingToolTabContentProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Mount Redux dispatcher
  const dispatch = useDispatch();

  // State for selected unit - initialize from URL param if provided
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(initialUnitId || null);
  const [proposedRentIncrease, setProposedRentIncrease] = useState<string | number>(0);
  const [proposedConcessions, setProposedConcessions] = useState<string | number>(0);
  const [proposedLeaseLength, setProposedLeaseLength] = useState<string | number>(12);
  const [selectedComparableTab, setSelectedComparableTab] = useState<number>(0);
  const [creationMessage, setCreationMessage] = useState<string | null>(null);
  const [showConcessionsAsMultiples, setShowConcessionsAsMultiples] = useState<boolean>(true);

  // Helper to get numeric value (treats empty string as 0)
  const getNumericValue = (value: string | number, defaultValue = 0): number => {
    if (value === '' || value === null || value === undefined) return defaultValue;
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? defaultValue : num;
  };

  // Create a map of unit ID to unit for quick lookup
  const unitMap = useMemo(() => {
    const map = new Map<string, Unit>();
    units.forEach((unit) => map.set(unit.id, unit));
    return map;
  }, [units]);

  // Create a map of unit ID to current lease
  const currentLeaseMap = useMemo(() => {
    if (!leases) return new Map<string, Lease>();
    const map = new Map<string, Lease>();
    leases.forEach((lease) => {
      // Only keep the most recent lease for each unit
      const existing = map.get(lease.unitId);
      if (!existing || dayjs(lease.leaseStartDate).isAfter(dayjs(existing.leaseStartDate))) {
        map.set(lease.unitId, lease);
      }
    });
    return map;
  }, [leases]);

  // Get units with current leases for dropdown
  const unitsWithLeases = useMemo(() => {
    return units.filter((unit) => currentLeaseMap.has(unit.id));
  }, [units, currentLeaseMap]);

  // Get selected unit and its current lease
  const selectedUnit = selectedUnitId ? unitMap.get(selectedUnitId) : null;
  const selectedLease = selectedUnitId ? currentLeaseMap.get(selectedUnitId) : null;

  // Helper to safely convert lease dates
  const getLeaseEndDate = (lease: Lease): Date | null => {
    return convertPseudoDateToRealDate(lease.leaseEndDate as any);
  };

  const getLeaseStartDate = (lease: Lease): Date | null => {
    return convertPseudoDateToRealDate(lease.leaseStartDate as any);
  };

  // Load draft lease for editing
  useEffect(() => {
    if (editingDraftLease && editingDraftLease.unitId) {
      setSelectedUnitId(editingDraftLease.unitId);

      // Calculate proposed rent increase from current lease
      const currentLease = currentLeaseMap.get(editingDraftLease.unitId);
      if (currentLease) {
        const rentIncrease = ((editingDraftLease.monthlyRent - currentLease.monthlyRent) / currentLease.monthlyRent) * 100;
        setProposedRentIncrease(Math.round(rentIncrease * 10) / 10);
      }

      // Set concessions as months (assuming concessions is the number of months)
      if (editingDraftLease.totalConcessionAmount && editingDraftLease.monthlyRent) {
        const concessionMonths = editingDraftLease.totalConcessionAmount / editingDraftLease.monthlyRent;
        setProposedConcessions(concessionMonths);
      }

      // Calculate lease length from dates
      const startDate = getLeaseStartDate(editingDraftLease);
      const endDate = getLeaseEndDate(editingDraftLease);
      if (startDate && endDate) {
        const lengthInMonths = dayjs(endDate).diff(dayjs(startDate), 'month');
        setProposedLeaseLength(lengthInMonths);
      }
    }
  }, [editingDraftLease, currentLeaseMap]);

  // Calculate current lease metrics
  const currentMetrics = useMemo(() => {
    if (!selectedLease || !selectedUnit) return null;

    const startDate = getLeaseStartDate(selectedLease);
    const endDate = getLeaseEndDate(selectedLease);
    if (!startDate || !endDate) return null;

    const leaseLength = dayjs(endDate).diff(dayjs(startDate), 'month');
    const sqft = selectedUnit.sqft || 1;

    const totalConcessions = Math.round((Array.isArray(selectedLease.concessions)
      ? selectedLease.concessions.reduce((sum: number, c: any) => sum + ((c.amount || 0) * selectedLease.monthlyRent), 0)
      : ((selectedLease.concessions || 0) * selectedLease.monthlyRent)) * 100) / 100;

    const effectiveMonthlyRent = leaseLength > 0 ? Math.round((selectedLease.monthlyRent - (totalConcessions / leaseLength)) * 100) / 100 : selectedLease.monthlyRent;
    const effectiveAnnualRent = effectiveMonthlyRent * 12;
    const effectiveAnnualRentPerSqft = Math.round((effectiveAnnualRent / sqft) * 100) / 100;
    const annualRent = selectedLease.monthlyRent * 12;
    const annualRentPerSqft = annualRent / sqft;

    return {
      monthlyRent: selectedLease.monthlyRent,
      leaseLength,
      concessions: totalConcessions,
      effectiveMonthlyRent,
      effectiveAnnualRent,
      effectiveAnnualRentPerSqft,
      annualRent,
      annualRentPerSqft,
      sqft,
    };
  }, [selectedLease, selectedUnit]);

  // Calculate proposed metrics
  const proposedMetrics = useMemo(() => {
    if (!currentMetrics) return null;

    const increaseNum = getNumericValue(proposedRentIncrease);
    const concessionNum = getNumericValue(proposedConcessions);
    const lengthNum = getNumericValue(proposedLeaseLength, 12);

    const proposedMonthlyRent = currentMetrics.monthlyRent * (1 + increaseNum / 100);
    const proposedConcessionAmount = concessionNum * proposedMonthlyRent;
    const proposedEffectiveMonthlyRent = proposedMonthlyRent - (proposedConcessionAmount / lengthNum);
    const proposedEffectiveAnnualRent = proposedEffectiveMonthlyRent * 12;
    const proposedEffectiveAnnualRentPerSqft = Math.round((proposedEffectiveAnnualRent / currentMetrics.sqft) * 100) / 100;
    const proposedAnnualRent = proposedMonthlyRent * 12;
    const proposedAnnualRentPerSqft = proposedAnnualRent / currentMetrics.sqft;

    const netIncreaseAmount = proposedEffectiveMonthlyRent - currentMetrics.effectiveMonthlyRent;
    const netIncreasePercent = (netIncreaseAmount / currentMetrics.effectiveMonthlyRent) * 100;

    return {
      monthlyRent: Math.round(proposedMonthlyRent * 100) / 100,
      leaseLength: lengthNum,
      concessions: Math.round(proposedConcessionAmount * 100) / 100,
      effectiveMonthlyRent: Math.round(proposedEffectiveMonthlyRent * 100) / 100,
      effectiveAnnualRent: Math.round(proposedEffectiveAnnualRent * 100) / 100,
      effectiveAnnualRentPerSqft: proposedEffectiveAnnualRentPerSqft,
      annualRent: Math.round(proposedAnnualRent * 100) / 100,
      annualRentPerSqft: Math.round(proposedAnnualRentPerSqft * 100) / 100,
      netIncreaseAmount: Math.round(netIncreaseAmount * 100) / 100,
      netIncreasePercent: Math.round(netIncreasePercent * 100) / 100,
    };
  }, [currentMetrics, proposedRentIncrease, proposedConcessions, proposedLeaseLength]);

  // Update building visualization when unit is selected
  useEffect(() => {
    if (!selectedUnit || !proposedMetrics) {
      dispatch(
        setBuildingVisual({
          apartmentData: [],
          visualizer: null,
        })
      );
      setTimeout(() => {
          dispatch(setHighlightedUnits([]));
        }, 100);
      return;
    }
    
   
    // Build visualization data for comparable units
    const comparableUnits = units.filter(
      (u) => u.id !== selectedUnit.id && currentLeaseMap.has(u.id)
    ).filter((u) => {
      // Simple comparability: same number of bedrooms
      return u.numBedrooms === selectedUnit.numBedrooms || u.line === selectedUnit.line;
    });

    // Get the proposed effective PPSF for color scaling
    const values = [proposedMetrics.effectiveAnnualRentPerSqft, ...comparableUnits
      .map((u) => {
        const lease = currentLeaseMap.get(u.id);
        if (!lease) return 0;
        return calculateEffectivePPSF(u, lease);
      })
      .filter((v) => v > 0)];

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const visualizer = new ColorGradeVisualizer(colorScales.default, minValue, maxValue);

    // Build apartment data with selected unit highlighted
    const apartmentData = [
      {
        apartmentId: selectedUnit.id,
        value: proposedMetrics.effectiveAnnualRentPerSqft,
        label: 'Proposed Effective PPSF',
      },
      ...comparableUnits
        .map((u) => {
          const lease = currentLeaseMap.get(u.id);
          if (!lease) return null;

          return {
            apartmentId: u.id,
            value: calculateEffectivePPSF(u, lease),
            label: 'Comparable PPSF',
          };
        })
        .filter(Boolean) as any[],
    ];

    
  //  dispatch(setHighlightedUnits([]));        
    dispatch(
      setBuildingVisual({
        apartmentData,
        visualizer,
      })
    );

    dispatch(
      setSelectedUnit({apartmentId: selectedUnit.id})
    )
  }, [selectedUnit, proposedMetrics, units, currentLeaseMap, dispatch]);

  useEffect(() => {
     // clear proposed metrics if no lease
     if (!editingDraftLease) {
      setProposedConcessions(0);
      setProposedLeaseLength(12);
      setProposedRentIncrease(0);
     }

  }, [selectedUnitId]);

  // Handle unit selection - update local state and notify parent to update URL
  const handleUnitSelect = (unitId: string | null) => {
    setSelectedUnitId(unitId);
    onSelectUnit(unitId);
  };

  // Handle export to spreadsheet
  const handleExportToSpreadsheet = async () => {
    if (!selectedUnit || !currentMetrics || !proposedMetrics) return;

    await exportSingleUnitToSpreadsheet(
      selectedUnit,
      currentMetrics,
      proposedMetrics,
      units,
      currentLeaseMap,
      leases
    );
  };

  // Create a draft lease from the proposed terms
  const handleCreateDraftLease = async () => {
    if (!selectedUnit || !selectedLease || !proposedMetrics) return;

    const proposedConcessionsArray = []
    // create draft concession - amount should be the number of months (multiplier of monthly rent)
    // NOT the dollar amount, since the calculation formula is: amount * monthlyRent
    if (proposedMetrics.concessions > 0) {
      // proposedMetrics.concessions is the dollar amount, so divide by monthlyRent to get months
      const concessionMonths = proposedMetrics.concessions / proposedMetrics.monthlyRent;
      proposedConcessionsArray.push({
        month: "draft",
        year: "draft",
        amount: concessionMonths,
        type: "rentMultiplier"
      });
    }

    try {
      // Check if a draft lease already exists for this unit
      const existingDraftLease = editingDraftLease && editingDraftLease.unitId === selectedUnit.id
        ? editingDraftLease
        : null;

      const {startDate, endDate} = calculateNewLeaseDates(selectedLease.leaseEndDate,{months:proposedMetrics.leaseLength});
      // Calculate start date as one day after the current lease end date
      
      // Create draft lease object with proposed metrics
      const draftLease: any = {
        ...selectedLease,
        // Use existing ID if updating, otherwise create new ID
        id: existingDraftLease?.id || `draft_${selectedUnit.id}_${Date.now()}`,
        status: 'draft',
        // Proposed terms override the original lease terms
        monthlyRent: proposedMetrics.monthlyRent,
        leaseStartDate: startDate,
        leaseEndDate: endDate,
        concessions: proposedConcessionsArray,
        totalConcessionAmount: proposedMetrics.concessions,
      };

      console.log(existingDraftLease ? 'Updating draft lease:' : 'Creating draft lease:', selectedLease, draftLease);

      // Call the parent callback to add/update the draft lease
      onCreateDraftLease?.(draftLease);
      saveLease(draftLease);

      setCreationMessage(`Draft lease ${existingDraftLease ? 'updated' : 'created'} for ${selectedUnit.name || selectedUnit.id}`);

      // Clear message after 3 seconds
      setTimeout(() => setCreationMessage(null), 3000);
    } catch (error) {
      console.error('Error creating/updating draft lease:', error);
      setCreationMessage(`Error creating/updating draft lease`);
      setTimeout(() => setCreationMessage(null), 3000);
    }
  };

  return (
    <div className="pricing-tool h-[calc(100vh-112px)] w-full flex flex-col p-8 overflow-y-auto" ref={ref}>
      {/* <Typography variant="h5" className="mb-6">
        Pricing Tool
      </Typography> */}

      {/* Unit Selection */}
      { !editingDraftLease ? (
      <div className="mb-8">
        <Autocomplete
          options={unitsWithLeases}
          getOptionLabel={(unit) => `${unit.name || unit.id} - ${unit.numBedrooms}BR, ${unit.sqft} sqft`}
          value={selectedUnit || null}
          onChange={(_, value) => handleUnitSelect(value?.id || null)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Unit"
              placeholder="Choose a unit with an active lease"
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          noOptionsText="No units with active leases found"
        />
      </div>
      ) : <div className="mb-8">
        <div className='flex flex-row items-center gap-8'>
        <Typography variant="subtitle1">
          Editing Draft Lease for {selectedUnit ? (selectedUnit.name || selectedUnit.id) : '...'}
        </Typography>
        <Button
          variant="text"
          size="small"
          onClick={() => {
            // Add your close handler here
            onStopEditingDraftLease && onStopEditingDraftLease();            
          }}
          sx={{ minWidth: 'auto', padding: '4px' }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </Button>
        </div>
      </div>}


      {creationMessage && (
        <Alert severity={creationMessage.includes('Error') ? 'error' : 'success'} className="mb-4">
          {creationMessage}
        </Alert>
      )}

      {/* Proposed Terms Inputs */}
      {selectedUnit && (
          <div className="border border-zinc-600 rounded-md p-4 flex-shrink-0 mb-4">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-4">
                <Typography variant="subtitle2" className="font-bold">
                  Proposed Terms Inputs
                </Typography>
                <ToggleButtonGroup
                  value={showConcessionsAsMultiples ? 'months' : 'dollars'}
                  exclusive
                  onChange={(_, value) => {
                    if (value !== null) {
                      setShowConcessionsAsMultiples(value === 'months');
                    }
                  }}
                  size="small"
                  sx={{ height: 28 }}
                >
                  <ToggleButton value="months" sx={{ fontSize: '0.7rem', px: 1.5 }}>
                    Conc. (mo)
                  </ToggleButton>
                  <ToggleButton value="dollars" sx={{ fontSize: '0.7rem', px: 1.5 }}>
                    Conc. ($)
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>
              <div className="flex gap-2">
                <Tooltip title="Export to Excel">
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={handleExportToSpreadsheet}
                    disabled={!proposedMetrics}
                  >
                    <FileDownloadIcon />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleCreateDraftLease}
                  disabled={!proposedMetrics}
                >
                  Create Draft Lease
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Typography variant="body2" color="textSecondary" className="mb-1">
                  Rent Increase (%)
                </Typography>
                <TextField
                  type="number"
                  value={proposedRentIncrease}
                  onChange={(e) => setProposedRentIncrease(e.target.value)}
                  inputProps={{ step: 0.1 }}
                  fullWidth
                  size="small"
                  helperText={currentMetrics ? `Current: $${getLongNumberStr(Math.round(currentMetrics.monthlyRent))} → Proposed: $${getLongNumberStr(Math.round(proposedMetrics?.monthlyRent || 0))}` : ''}
                />
              </div>
              <div>
                <Typography variant="body2" color="textSecondary" className="mb-1">
                  Concessions (Months)
                </Typography>
                <TextField
                  type="number"
                  value={proposedConcessions}
                  onChange={(e) => setProposedConcessions(e.target.value)}
                  inputProps={{ step: 0.5 }}
                  fullWidth
                  size="small"
                />
              </div>
              <div>
                <Typography variant="body2" color="textSecondary" className="mb-1">
                  Lease Length (Months)
                </Typography>
                <TextField
                  type="number"
                  value={proposedLeaseLength}
                  onChange={(e) => setProposedLeaseLength(e.target.value)}
                  fullWidth
                  size="small"
                />
              </div>
            </div>
          </div>
      )}

      {selectedUnit && currentMetrics && proposedMetrics ? (
        <div className="flex flex-col gap-6 flex-1">
          {/* Comparable Groups with Tabs (includes Before/After and Market Trends) */}
          <div className="border border-zinc-600 rounded-md overflow-hidden flex flex-col flex-1">
            <Box sx={{ flexShrink: 0 }}>
              <ComparablesSection
                selectedUnit={selectedUnit}
                allUnits={units}
                leaseMap={currentLeaseMap}
                allLeases={leases}
                proposedMetrics={proposedMetrics}
                currentMetrics={currentMetrics || undefined}
                selectedTabIndex={selectedComparableTab}
                hoveredUnitId={hoveredUnitId}
                onHoverUnit={onHoverUnit}
                showConcessionsAsMultiples={showConcessionsAsMultiples}
                onVisualizationGroupChange={(unitIds, groupName) => {
                  // Update building visualization with the selected group
                  const apartmentData = unitIds.map((id) => {
                    const u = units.find((unit) => unit.id === id);
                    if (!u) return null;

                    const lease = currentLeaseMap.get(u.id);
                    if (!lease) return null;

                    return {
                      apartmentId: u.id,
                      value: calculateEffectivePPSF(u, lease),
                      label: `Effective PPSF`,
                    };
                  }).filter(Boolean) as any[];

                  // Add proposed unit at the beginning
                  if (proposedMetrics && selectedUnit) {
                    apartmentData.unshift({
                      apartmentId: selectedUnit.id,
                      value: proposedMetrics.effectiveAnnualRentPerSqft,
                      label: 'Proposed Effective PPSF',
                    });
                  }

                  // Create visualizer for the group
                  const values = apartmentData.map((d) => d.value);
                  if (values.length > 0) {
                    const minValue = Math.min(...values);
                    const maxValue = Math.max(...values);
                    const visualizer = new ColorGradeVisualizer(colorScales.default, minValue, maxValue);
                    withLegendConfig(visualizer, commonLegendConfigs.ppsqf);

                    dispatch(
                      setBuildingVisual({
                        apartmentData,
                        visualizer,
                      })
                    );

                     setTimeout(() => {
                      dispatch(setHighlightedUnits(apartmentData.filter(ad => ad.value > 0)));
                    }, 100);
                  }
                }}
              />
            </Box>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center flex-1">
          <Typography variant="h6" color="textSecondary">
            {unitsWithLeases.length === 0
              ? 'No units with active leases found'
              : 'Select a unit to get started'}
          </Typography>
        </div>
      )}
    </div>
  );
}
